
import React, { createContext, useContext, useMemo, useState, useEffect, useRef, useCallback } from 'react';
import * as authService from '../services/authService';
import { refreshSession } from '../services/authService';
import authClient from '../services/authClient';

const AuthContext = createContext(undefined);

// ========================================
// AUTH PROVIDER
// Implements cookie-based authentication with CSRF
// Per DUAL_AUTH_IMPLEMENTATION.md specification
// ========================================

export function AuthProvider({ children }) {
  const [state, setState] = useState({
    user: null,
    booting: true,
  });
  
  // Track if auth has been initialized to prevent re-initialization loops
  const hasInitialized = useRef(false);
  const initializingRef = useRef(false);

  // Initialize authentication on mount
  useEffect(() => {
    let ignore = false;
    
    async function initAuth() {
      // Prevent concurrent initialization
      if (initializingRef.current) {
        console.log('⏳ [AuthContext] Initialization already in progress, skipping');
        return;
      }
      
      initializingRef.current = true;
      console.log('🚀 [AuthContext] Initializing authentication...');
      setState(s => ({ ...s, booting: true }));
      
      try {
        // 1. Do NOT fetch CSRF token proactively when no user is present
        // To prevent background requests while logged out, only obtain CSRF
        // when we detect stored user data (i.e., a returning session) or
        // during an explicit login flow.
        
        // 2. For cookie-based auth, check localStorage for user data
        // No need to validate token - backend validates cookies automatically
        console.log('🔍 [AuthContext] Step 2: Checking local user data...');
        const storedUser = localStorage.getItem('user');
        
        if (ignore) return;
        
        if (storedUser) {
          // Only fetch CSRF if we have stored user data (returning user)
          const existingCsrf = sessionStorage.getItem('csrfToken');
          if (!existingCsrf) {
            console.log('📋 [AuthContext] Stored user detected; fetching CSRF token...');
            await authService.getCsrfToken();
            console.log('✅ [AuthContext] CSRF token ready');
          }
          try {
            const user = JSON.parse(storedUser);
            console.log('✅ [AuthContext] User found in storage:', user.email);
            setState({ user, booting: false });
          } catch (parseError) {
            console.warn('⚠️ [AuthContext] Invalid user data in storage');
            localStorage.removeItem('user');
            setState({ user: null, booting: false });
          }
        } else {
          console.log('ℹ️ [AuthContext] No user data in storage');
          setState({ user: null, booting: false });
        }
        
        hasInitialized.current = true;
      } catch (error) {
        console.error('❌ [AuthContext] Auth initialization error:', error);
        if (!ignore) {
          setState({ user: null, booting: false });
        }
      } finally {
        initializingRef.current = false;
      }
    }
    
    initAuth();
    
    // Listen for auth events from other tabs or refresh
    // Note: auth:updated only re-checks user data, doesn't refetch CSRF
    const onAuthUpdated = () => {
      console.log('🔄 [AuthContext] auth:updated received');
      // Only re-read user data from localStorage, don't reinitialize fully
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          setState(s => ({ ...s, user }));
        } catch {
          setState(s => ({ ...s, user: null }));
        }
      }
    };
    
    const PUBLIC_PATHS = ['/', '/privacy', '/terms', '/contact', '/faq'];

    const onLogout = () => {
      console.log('🚪 [AuthContext] auth:logout received');
      setState({ user: null, booting: false });
      const currentPath = window.location.pathname;
      try {
        if (!PUBLIC_PATHS.includes(currentPath) && currentPath !== '/login') {
          window.location.href = '/login';
        }
      } catch {}
    };

    const onUnauthorized = () => {
      console.log('⛔ [AuthContext] auth:unauthorized received');
      localStorage.removeItem('user');
      setState({ user: null, booting: false });
      const currentPath = window.location.pathname;
      try {
        if (!PUBLIC_PATHS.includes(currentPath) && currentPath !== '/login') {
          window.location.href = '/login';
        }
      } catch {}
    };
    
    window.addEventListener('auth:updated', onAuthUpdated);
    window.addEventListener('auth:logout', onLogout);
    window.addEventListener('auth:unauthorized', onUnauthorized);
    
    return () => {
      ignore = true;
      window.removeEventListener('auth:updated', onAuthUpdated);
      window.removeEventListener('auth:logout', onLogout);
      window.removeEventListener('auth:unauthorized', onUnauthorized);
    };
  }, []);

  const login = useCallback(({ user }) => {
    setState({ user, booting: false });
  }, []);

  const logout = useCallback(async () => {
    try {
      // Call logout service to clear cookies and server-side session
      await authService.logout();
      
      // Clear local auth state
      setState({ user: null, booting: false });

      // Force navigation to login page
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout failed:', error);
      // Still clear local state and redirect even if server call fails
      setState({ user: null, booting: false });
      window.location.href = '/login';
    }
  }, []);

  const value = useMemo(() => ({
    isAuthenticated: Boolean(state.user),
    user: state.user,
    booting: Boolean(state.booting),
    login,
    logout,
  }), [state, login, logout]);
  
  // Proactive refresh: refresh when access token expiry is within 5 minutes
  // Improvements: stop proactive refresh when user is idle for configurable threshold
  // and add backoff on repeated failures so we don't hammer backend during outages.
  React.useEffect(() => {
    let timer = null;
    const checkInterval = 60 * 1000; // check every minute

    // Idle threshold: stop proactive refresh if no user activity for this period
    const IDLE_THRESHOLD_MS = parseInt(process.env.REACT_APP_AUTH_IDLE_THRESHOLD_MS, 10) || (30 * 60 * 1000); // 30 minutes

    // Backoff refs to avoid frequent retry after failures
    const backoffRef = { count: 0, until: 0 };

    // Track last user activity (shared across tabs via localStorage)
    const ACTIVITY_KEY = 'auth:lastActive';
    const updateLastActive = () => {
      try { localStorage.setItem(ACTIVITY_KEY, String(Date.now())); } catch {}
    };
    const getLastActive = () => {
      try { return Number(localStorage.getItem(ACTIVITY_KEY)) || 0; } catch { return 0; }
    };

    // Mark initial activity now (so freshly-logged-in users are considered active)
    updateLastActive();

    const userActivityHandler = () => updateLastActive();

    const isTabVisible = () => (typeof document === 'undefined' ? true : !document.hidden);

    const shouldRun = () => {
      if (!state.user) return false;
      if (!isTabVisible()) return false;
      const last = getLastActive();
      if (!last) return true;
      const idle = Date.now() - last;
      if (idle > IDLE_THRESHOLD_MS) return false;
      // If we are in a backoff window, skip
      if (Date.now() < backoffRef.until) return false;
      return true;
    };

    const computeBackoff = (count) => {
      const base = 60 * 1000; // 1 minute
      const max = 30 * 60 * 1000; // 30 minutes
      const val = Math.min(base * Math.pow(2, Math.max(0, count - 1)), max);
      return val;
    };

    const runCheck = async () => {
      try {
        // If we are backed off, skip
        if (Date.now() < backoffRef.until) return;

        const expiry = authClient.getAccessTokenExpiry();
        if (!expiry) return;
        if (Date.now() >= expiry) {
          await refreshSession();
          // on success reset backoff
          backoffRef.count = 0;
          backoffRef.until = 0;
          return;
        }
        const remaining = expiry - Date.now();
        if (remaining < 5 * 60 * 1000) {
          try {
            await refreshSession();
            backoffRef.count = 0;
            backoffRef.until = 0;
          } catch (e) {
            // Increment backoff on failure
            backoffRef.count = Math.min(backoffRef.count + 1, 10);
            backoffRef.until = Date.now() + computeBackoff(backoffRef.count);
          }
        }
      } catch (e) {
        // ignore - refreshSession will dispatch auth:unauthorized on failure
      }
    };

    const startTimer = () => {
      if (timer) return;
      // immediate run then interval
      runCheck();
      timer = setInterval(() => {
        if (shouldRun()) runCheck();
      }, checkInterval);
    };

    const stopTimer = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    const onVisibility = () => {
      // When tab becomes visible, check if we were idle and validate once before resuming
      if (typeof document !== 'undefined' && !document.hidden) {
        const last = getLastActive();
        const idle = last ? (Date.now() - last) : 0;
        if (idle > IDLE_THRESHOLD_MS) {
          // We were idle. Validate session once; resume only if valid.
          (async () => {
            try {
              const v = await authService.validateToken();
              if (v?.ok) {
                // update activity and resume timer
                updateLastActive();
                if (shouldRun()) {
                  runCheck();
                  startTimer();
                }
              } else {
                try { window.dispatchEvent(new CustomEvent('auth:unauthorized', { detail: { reason: 'session_invalid' } })); } catch {}
              }
            } catch (e) {}
          })();
          return;
        }
      }

      if (shouldRun()) {
        // on focus/visible, trigger immediate refresh
        runCheck();
        startTimer();
      } else {
        stopTimer();
      }
    };

    // Attach lightweight user activity listeners to detect idle state
    window.addEventListener && window.addEventListener('mousemove', userActivityHandler);
    window.addEventListener && window.addEventListener('keydown', userActivityHandler);
    window.addEventListener && window.addEventListener('touchstart', userActivityHandler);

    // Start/stop based on initial conditions
    if (shouldRun()) startTimer();

    document.addEventListener && document.addEventListener('visibilitychange', onVisibility);

    // When tab becomes visible after idle, validate session once and resume if valid
    const onStorage = (ev) => {
      if (ev && ev.key === ACTIVITY_KEY) {
        // someone updated activity in another tab
        if (shouldRun() && !timer) {
          // validate session before resuming active refreshes
          (async () => {
            try {
              const v = await authService.validateToken();
              if (v?.ok) {
                // resume timer and run immediate check
                startTimer();
                runCheck();
              } else {
                // if not valid, dispatch unauthorized to clear state
                try { window.dispatchEvent(new CustomEvent('auth:unauthorized', { detail: { reason: 'session_invalid' } })); } catch {}
              }
            } catch {}
          })();
        }
      }
    };

    window.addEventListener && window.addEventListener('storage', onStorage);

    return () => {
      stopTimer();
      document.removeEventListener && document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener && window.removeEventListener('mousemove', userActivityHandler);
      window.removeEventListener && window.removeEventListener('keydown', userActivityHandler);
      window.removeEventListener && window.removeEventListener('touchstart', userActivityHandler);
      window.removeEventListener && window.removeEventListener('storage', onStorage);
    };
  }, [state.user]);
  
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
